import { type ButtonHTMLAttributes, type MouseEvent, type ReactNode, useState } from 'react';

import { cn } from '@shared/lib/cn';

import { PRESSED_CLASS, SIZE_CLASS } from './toggle-button.constant';

export type ToggleButtonSize = 'xs' | 'sm' | 'md';

export interface ToggleButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'onChange'
> {
  /** Figma `pressed`(제어). 주면 `onPressedChange` 로 바꾼다 */
  pressed?: boolean;
  /** 제어하지 않을 때의 처음 값 */
  defaultPressed?: boolean;
  onPressedChange?: (pressed: boolean) => void;
  /** Figma `Show icon` + `Icon`. 글자 앞 아이콘(`<Icon name="…" size="sm" />`) */
  icon?: ReactNode;
  /** Figma `size`(xs 24 · sm 32 · md 40). 기본 sm */
  size?: ToggleButtonSize;
  /** Figma `Label` */
  children?: ReactNode;
  /** 고정 너비는 부르는 쪽이 준다: `className="w-(--size-button-md)"` */
  className?: string;
}

/** 켜고 끄는 글자 버튼. 켜짐 = primary 테두리 · 글자(`aria-pressed`) */
export function ToggleButton({
  pressed,
  defaultPressed = false,
  onPressedChange,
  icon,
  size = 'sm',
  children,
  className,
  type = 'button',
  onClick,
  ...rest
}: ToggleButtonProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultPressed);
  const isControlled = pressed !== undefined;
  const on = isControlled ? pressed : uncontrolled;

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (!isControlled) setUncontrolled(!on);
    onPressedChange?.(!on);
  }

  return (
    <button
      type={type}
      aria-pressed={on}
      data-size={size}
      onClick={handleClick}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-sm border whitespace-nowrap select-none disabled:cursor-not-allowed',
        on ? PRESSED_CLASS.true : PRESSED_CLASS.false,
        SIZE_CLASS[size],
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span data-slot="icon" className="inline-flex shrink-0">
          {icon}
        </span>
      ) : null}
      {children}
    </button>
  );
}
